import { useEffect, useRef, useState } from 'react'

/**
 * Liste déroulante teintée — TRANSPOSITION du dropdown maison de la console
 * d'origine (bloc `.cs` / `.cs-trigger` / `.cs-panel` / `.cs-opt`).
 *
 * ⚠️ CE N'EST PAS UN `<select>` NATIF. L'original écrivait bien `<select>` dans
 * son markup, mais un script global les balayait tous au chargement pour les
 * masquer (`opacity:0`) et dessiner par-dessus ce menu-ci. En cherchant du CSS
 * de personnalisation de `<select>` je ne trouvais rien — la personnalisation
 * était en JavaScript, et le premier portage l'a donc entièrement manquée : on
 * se retrouvait avec le menu gris du système à la place du menu or.
 *
 * Le `<select>` reste présent, masqué, pour que le champ garde un nom, reste
 * atteignable au clavier et soit lu par les lecteurs d'écran — c'est ce que
 * faisait l'original.
 */
export type OptionListe = { valeur: string; libelle: string }

export function ListeDeroulante({
  valeur,
  options,
  surChangement,
  pleineLargeur = false,
  desactive = false,
  nom,
}: {
  valeur: string
  options: OptionListe[]
  surChangement: (valeur: string) => void
  /** Dans une modale, `.modal select{width:100%}` donnait un champ pleine largeur. */
  pleineLargeur?: boolean
  desactive?: boolean
  nom?: string
}) {
  const [ouvert, setOuvert] = useState(false)
  const racine = useRef<HTMLDivElement>(null)
  const panneau = useRef<HTMLDivElement>(null)

  // Un clic n'importe où ailleurs referme, comme dans l'original — le
  // déclencheur et les options arrêtent la propagation pour ne pas se refermer
  // eux-mêmes.
  useEffect(() => {
    if (!ouvert) return
    const fermer = () => setOuvert(false)
    const echap = (e: KeyboardEvent) => e.key === 'Escape' && setOuvert(false)
    document.addEventListener('click', fermer)
    document.addEventListener('keydown', echap)
    return () => {
      document.removeEventListener('click', fermer)
      document.removeEventListener('keydown', echap)
    }
  }, [ouvert])

  // À l'ouverture, l'option courante est ramenée dans le champ de vision : sur
  // une liste de vingt sections, elle serait sinon hors écran.
  useEffect(() => {
    if (ouvert) panneau.current?.querySelector('.cs-opt.sel')?.scrollIntoView({ block: 'nearest' })
  }, [ouvert])

  const courante = options.find((o) => o.valeur === valeur)

  return (
    <div
      className={`cs${ouvert ? ' open' : ''}${pleineLargeur ? ' cs-pleine' : ''}`}
      ref={racine}
    >
      <select
        name={nom}
        value={valeur}
        disabled={desactive}
        onChange={(e) => surChangement(e.target.value)}
        tabIndex={-1}
        aria-hidden="true"
      >
        {options.map((o) => (
          <option key={o.valeur} value={o.valeur}>
            {o.libelle}
          </option>
        ))}
      </select>

      <button
        type="button"
        className="cs-trigger"
        disabled={desactive}
        onClick={(e) => {
          e.stopPropagation()
          setOuvert((o) => !o)
        }}
      >
        <span className="cs-tx">{courante?.libelle ?? ''}</span>
        <svg
          className="cs-cv"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {/* Réserve la largeur du libellé le plus long — voir le commentaire de
          `.cs-mesure` dans styles.css. */}
      <span className="cs-mesure" aria-hidden="true">
        {options.map((o) => (
          <span key={o.valeur}>{o.libelle}</span>
        ))}
      </span>

      <div className="cs-panel" ref={panneau}>
        {options.map((o) => (
          <div
            key={o.valeur}
            className={`cs-opt${o.valeur === valeur ? ' sel' : ''}`}
            onClick={(e) => {
              e.stopPropagation()
              surChangement(o.valeur)
              setOuvert(false)
            }}
          >
            {o.libelle}
          </div>
        ))}
      </div>
    </div>
  )
}
